/**
 * Language mapping utilities
 * Maps between backend language codes (en, ja, br) and i18n language codes (en, ja, pt-BR)
 */

export type BackendLanguageCode = 'en' | 'ja' | 'br' | 'km' | 'tw';
export type I18nLanguageCode = 'en' | 'ja' | 'pt-BR' | 'km' | 'zh-TW';

/**
 * Maps backend language code to i18n language code
 * @param backendCode - Language code from backend (en, ja, br)
 * @returns i18n language code (en, ja, pt-BR)
 */
export const mapBackendToI18n = (backendCode: string): I18nLanguageCode => {
  switch (backendCode.toLowerCase()) {
    case 'br':
      return 'pt-BR';
    case 'ja':
      return 'ja';
    case 'km':
      return 'km';
    case 'tw':
      return 'zh-TW';
    case 'en':
    default:
      return 'en';
  }
};

/**
 * Maps i18n language code to backend language code
 * @param i18nCode - i18n language code (en, ja, pt-BR)
 * @returns Backend language code (en, ja, br)
 */
export const mapI18nToBackend = (i18nCode: string): BackendLanguageCode => {
  switch (i18nCode.toLowerCase()) {
    case 'pt-br':
      return 'br';
    case 'ja':
      return 'ja';
    case 'km':
      return 'km';
    case 'zh-tw':
      return 'tw';
    case 'en':
    default:
      return 'en';
  }
};

/**
 * Validates if a language code is supported
 * @param code - Language code to validate
 * @returns true if supported, false otherwise
 */
export const isSupportedLanguage = (code: string): boolean => {
  const supportedI18n: I18nLanguageCode[] = ['en', 'ja', 'pt-BR', 'km', 'zh-TW'];
  const supportedBackend: BackendLanguageCode[] = ['en', 'ja', 'br', 'km', 'tw'];

  return supportedI18n.includes(code as I18nLanguageCode) ||
    supportedBackend.includes(code as BackendLanguageCode);
};
